import React, { createContext, useContext, useState, useEffect } from 'react';
import { Volume2, VolumeX } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';

const SoundContext = createContext();

export const useSounds = () => {
  const context = useContext(SoundContext);
  if (!context) {
    throw new Error('useSounds must be used within SoundProvider');
  }
  return context;
};

// Web Audio API sound generator
const createBeep = (frequency = 800, duration = 0.1) => {
  const audioContext = new (window.AudioContext || window.webkitAudioContext)();
  const oscillator = audioContext.createOscillator();
  const gainNode = audioContext.createGain();

  oscillator.connect(gainNode);
  gainNode.connect(audioContext.destination);

  oscillator.frequency.value = frequency;
  oscillator.type = 'sine';

  gainNode.gain.setValueAtTime(0.3, audioContext.currentTime);
  gainNode.gain.exponentialRampToValueAtTime(0.01, audioContext.currentTime + duration);

  oscillator.start(audioContext.currentTime);
  oscillator.stop(audioContext.currentTime + duration);
};

const createWhoosh = () => {
  const audioContext = new (window.AudioContext || window.webkitAudioContext)();
  const oscillator = audioContext.createOscillator();
  const gainNode = audioContext.createGain();
  const filter = audioContext.createBiquadFilter();

  oscillator.connect(filter);
  filter.connect(gainNode);
  gainNode.connect(audioContext.destination);

  oscillator.type = 'sawtooth';
  oscillator.frequency.setValueAtTime(400, audioContext.currentTime);
  oscillator.frequency.exponentialRampToValueAtTime(50, audioContext.currentTime + 0.3);

  filter.type = 'lowpass';
  filter.frequency.setValueAtTime(1000, audioContext.currentTime);

  gainNode.gain.setValueAtTime(0.2, audioContext.currentTime);
  gainNode.gain.exponentialRampToValueAtTime(0.01, audioContext.currentTime + 0.3);

  oscillator.start(audioContext.currentTime);
  oscillator.stop(audioContext.currentTime + 0.3);
};

export const SoundProvider = ({ children }) => {
  const [soundEnabled, setSoundEnabled] = useState(false);
  const [showNotification, setShowNotification] = useState(false);

  useEffect(() => {
    // Load sound preference from localStorage
    const savedPreference = localStorage.getItem('soundEnabled');
    if (savedPreference !== null) {
      setSoundEnabled(savedPreference === 'true');
    }
  }, []);

  useEffect(() => {
    // Save sound preference to localStorage
    localStorage.setItem('soundEnabled', soundEnabled);
  }, [soundEnabled]);

  const playSound = (type) => {
    if (!soundEnabled) return;

    try {
      switch (type) {
        case 'click':
          createBeep(1200, 0.08);
          break;
        case 'hover':
          createBeep(900, 0.05);
          break;
        case 'whoosh':
          createWhoosh();
          break;
        case 'success':
          createBeep(1400, 0.1);
          setTimeout(() => createBeep(1600, 0.1), 100);
          break;
        default:
          createBeep();
      }
    } catch (error) {
      console.log('Audio playback failed:', error);
    }
  };

  const toggleSound = () => {
    const newState = !soundEnabled;
    setSoundEnabled(newState);
    
    // Play confirmation sound
    if (newState) {
      setTimeout(() => createBeep(1200, 0.1), 100);
    }

    // Show notification
    setShowNotification(true);
    setTimeout(() => setShowNotification(false), 2000);
  };

  return (
    <SoundContext.Provider value={{ soundEnabled, playSound, toggleSound }}>
      {children}
      
      {/* Sound Toggle Button */}
      <motion.button
        whileHover={{ scale: 1.1 }}
        whileTap={{ scale: 0.95 }}
        onClick={toggleSound}
        className="fixed bottom-6 right-6 z-50 w-12 h-12 bg-gradient-to-br from-cyan-500 to-blue-600 rounded-full flex items-center justify-center shadow-lg neon-glow-icon"
        aria-label="Toggle sound effects"
      >
        {soundEnabled ? (
          <Volume2 size={20} className="text-white" />
        ) : (
          <VolumeX size={20} className="text-white" />
        )}
      </motion.button>

      {/* Notification */}
      <AnimatePresence>
        {showNotification && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 20 }}
            className="fixed bottom-24 right-6 z-50 px-4 py-2 bg-slate-800/90 backdrop-blur-sm border border-cyan-500/30 rounded-lg"
          >
            <p className="text-sm text-cyan-400 font-semibold">
              Sound effects {soundEnabled ? 'enabled' : 'disabled'}
            </p>
          </motion.div>
        )}
      </AnimatePresence>
    </SoundContext.Provider>
  );
};
